import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  MoreVertical,
  Flag,
  Paperclip,
  Clock,
  MapPin,
  Calendar,
  X,
  CheckCircle,
  Circle,
  Download,
  File,
} from "lucide-react";
import useTask from "../../hooks/useTask";
import useEdrive from "../../hooks/useEdrive";
import TaskModal from "./TaskModal";
import FileSelectorModal from "./FileSelectorModal";
import AlertModal from "./AlertModal";

/* ===== 유틸 함수 (전역) ===== */
const getCategoryLabel = (category) => {
  const labels = {
    work: "업무",
    meeting: "회의",
    deadline: "마감일",
    personal: "개인",
    other: "기타",
  };
  return labels[category] || category;
};

const getTimeDisplay = (startTime, endTime, isAllDay) => {
  if (isAllDay) return "하루 종일";
  if (startTime) {
    const extractTime = (timeStr) => {
      if (!timeStr) return "";
      const timePart = timeStr.split("T")[1] || timeStr;
      return timePart.substring(0, 5);
    };

    const start = extractTime(startTime);
    const end = endTime ? extractTime(endTime) : "";
    return end ? `${start} - ${end}` : start;
  }
  return "";
};

/**
 * 캘린더 뷰 컴포넌트
 * @param {Array} selectedCalendars - 선택된 캘린더 ID 배열
 */
export default function CalendarView({ selectedCalendars = [] }) {
  /* ===== 훅/상태 ===== */
  const {
    fetchMonthlyTasks,
    deleteTask,
    fetchTaskDetail,
    createTask,
    updateTask,
  } = useTask();
  const edrive = useEdrive();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(false);

  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  /* ===== 효과 ===== */
  useEffect(() => {
    if (selectedCalendars.length > 0) {
      loadMonthlyTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, selectedCalendars]);

  /* ===== 데이터 로드 & 내비게이션 ===== */
  const loadMonthlyTasks = async () => {
    setLoading(true);
    const result = await fetchMonthlyTasks(
      selectedCalendars,
      currentDate.getFullYear(),
      currentDate.getMonth() + 1
    );
    if (result.ok) {
      setEvents(result.data.by_calendar || {});
    }
    setLoading(false);
  };

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // 이전 달 날짜들
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: prevMonthLastDay - i,
        isCurrentMonth: false,
        fullDate: new Date(year, month - 1, prevMonthLastDay - i),
      });
    }

    // 현재 달 날짜들
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: i,
        isCurrentMonth: true,
        fullDate: new Date(year, month, i),
      });
    }

    // 다음 달 날짜들
    const remainingDays = 42 - days.length; // 6주 * 7일
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: i,
        isCurrentMonth: false,
        fullDate: new Date(year, month + 1, i),
      });
    }

    return days;
  };

  /* ===== 유틸/핸들러 (컴포넌트 내부) ===== */
  const getEventsForDate = (date) => {
    // 로컬 기준으로 YYYY-MM-DD 문자열 생성 (타임존 이슈 회피)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    const allEvents = [];
    Object.entries(events).forEach(([_, calendarEvents]) => {
      const dayEvents = calendarEvents.filter(
        (event) => event.event_date === dateStr
      );
      allEvents.push(...dayEvents);
    });

    return allEvents;
  };

  const handleDateClick = (day) => {
    if (day.isCurrentMonth) {
      setSelectedDate(day.fullDate);
      setSelectedTask(null);
      setShowTaskModal(true);
    }
  };

  const handleTaskClick = async (task, e) => {
    e.stopPropagation();
    setSelectedTask(task);
    setShowDetailModal(true);
    setLoadingDetail(true);

    const result = await fetchTaskDetail({
      task_id: task.id,
      is_virtual_event: task.event_type === "recurring_virtual",
      recurring_event_id: task.recurring_event_id || "",
      date: task.event_date,
    });

    if (result.ok) {
      setSelectedTaskDetail(result.detail);
    }
    setLoadingDetail(false);
  };

  const handleEditTask = () => {
    setShowDetailModal(false);
    setShowTaskModal(true);
  };

  const handleFileDownload = async (file) => {
    const blob = await edrive.viewFile(file.file_id);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleTaskSave = async (taskData) => {
    if (selectedTask) {
      // 수정
      const result = await updateTask({
        ...taskData,
        id: selectedTask.id,
        is_event: selectedTask.event_type === "recurring_virtual",
        attendee_files: selectedFiles.map((f) => f.id),
      });

      if (result.ok) {
        setShowTaskModal(false);
        setSelectedFiles([]);
        loadMonthlyTasks();
      }
    } else {
      // 생성
      const result = await createTask({
        ...taskData,
        attendee_files: selectedFiles.map((f) => f.id),
      });

      if (result.ok) {
        setShowTaskModal(false);
        setSelectedFiles([]);
        loadMonthlyTasks();
      }
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    const result = await deleteTask({
      calendar_id: taskToDelete.calendar_id,
      id: taskToDelete.id,
      is_recurring: taskToDelete.event_type === "recurring_virtual",
      date: taskToDelete.event_date,
    });

    if (result.ok) {
      loadMonthlyTasks();
      setShowDeleteAlert(false);
      setTaskToDelete(null);
    } else if (result.viewOnly) {
      setShowDeleteAlert(false);
      alert("읽기 권한만 있어 삭제할 수 없습니다.");
    }
  };

  const getPriorityIcon = (priority) => {
    const colors = {
      high: "text-red-500",
      medium: "text-yellow-500",
      low: "text-gray-400",
    };
    return <Flag className={`w-3 h-3 ${colors[priority] || colors.low}`} />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      work: "bg-blue-100 text-blue-800",
      meeting: "bg-purple-100 text-purple-800",
      deadline: "bg-red-100 text-red-800",
      personal: "bg-green-100 text-green-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colors[category] || colors.other;
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  /* ===== 디테일 모달 (내부 컴포넌트) ===== */
  const TaskDetailModal = () => {
    if (!showDetailModal || !selectedTask) return null;
    const detail = selectedTaskDetail;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            {/* 헤더 */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {selectedTask.title}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span
                    className={`font-medium ${
                      selectedTask.priority === "high"
                        ? "text-red-500"
                        : selectedTask.priority === "medium"
                        ? "text-yellow-500"
                        : "text-gray-500"
                    }`}
                  >
                    {selectedTask.priority === "high"
                      ? "높음"
                      : selectedTask.priority === "medium"
                      ? "보통"
                      : "낮음"}
                  </span>
                  <span
                    className={`px-2 py-1 rounded ${getCategoryColor(
                      selectedTask.category
                    )}`}
                  >
                    {getCategoryLabel(selectedTask.category)}
                  </span>
                  <span
                    className={`px-2 py-1 rounded ${
                      selectedTask.status === "completed"
                        ? "bg-green-100 text-green-700"
                        : selectedTask.status === "cancelled"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {selectedTask.status === "completed"
                      ? "완료됨"
                      : selectedTask.status === "cancelled"
                      ? "취소됨"
                      : "진행 중"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedTaskDetail(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 상세 정보 */}
            <div className="space-y-4">
              {/* 날짜 */}
              <div className="flex items-center text-gray-700">
                <Calendar className="w-5 h-5 mr-3 text-gray-400" />
                <span>{selectedTask.event_date}</span>
              </div>

              {/* 시간 */}
              <div className="flex items-center text-gray-700">
                <Clock className="w-5 h-5 mr-3 text-gray-400" />
                <span>
                  {getTimeDisplay(
                    selectedTask.start_time,
                    selectedTask.end_time,
                    selectedTask.is_all_day
                  )}
                </span>
              </div>

              {/* 위치 */}
              {selectedTask.location && (
                <div className="flex items-center text-gray-700">
                  <MapPin className="w-5 h-5 mr-3 text-gray-400" />
                  <span>{selectedTask.location}</span>
                </div>
              )}

              {/* 설명 */}
              {detail?.description && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">설명</h4>
                  <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                    {detail.description}
                  </p>
                </div>
              )}

              {/* 첨부파일 */}
              {loadingDetail ? (
                <div className="text-sm text-gray-500">첨부파일 로딩 중...</div>
              ) : (
                detail?.attachments &&
                detail.attachments.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                      <Paperclip className="w-4 h-4 mr-1" />
                      첨부파일 ({detail.attachments.length})
                    </h4>
                    <div className="space-y-2">
                      {detail.attachments.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center p-3 bg-gray-50 rounded hover:bg-gray-100"
                        >
                          <File className="w-5 h-5 text-gray-500 mr-3" />
                          <span className="text-sm text-gray-700 flex-1">
                            {file.file_name}
                          </span>
                          <span className="text-xs text-gray-500 mr-3">
                            {(file.file_size / 1024 / 1024).toFixed(2)} MB
                          </span>
                          <button
                            onClick={() => handleFileDownload(file)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="다운로드"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}

              {/* 반복 정보 */}
              {detail?.recurring_info && (
                <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                  <p className="font-medium text-blue-700">반복 일정</p>
                  <p>
                    {detail.recurring_info.repeat_type === "daily"
                      ? "매일"
                      : detail.recurring_info.repeat_type === "weekly"
                      ? "매주"
                      : detail.recurring_info.repeat_type === "monthly"
                      ? "매월"
                      : "매년"}
                    {detail.recurring_info.repeat_interval > 1 &&
                      ` ${detail.recurring_info.repeat_interval}회마다`}
                  </p>
                  {detail.recurring_info.repeat_end_date && (
                    <p>
                      종료일:{" "}
                      {new Date(
                        detail.recurring_info.repeat_end_date
                      ).toLocaleDateString("ko-KR")}
                    </p>
                  )}
                </div>
              )}

              {/* 생성/수정 시간 */}
              {detail && (
                <div className="text-sm text-gray-500 pt-4 border-t">
                  <p>
                    생성: {new Date(detail.created_at).toLocaleString("ko-KR")}
                  </p>
                  {detail.updated_at && (
                    <p>
                      수정:{" "}
                      {new Date(detail.updated_at).toLocaleString("ko-KR")}
                    </p>
                  )}
                  {detail.completed_at && (
                    <p>
                      완료:{" "}
                      {new Date(detail.completed_at).toLocaleString("ko-KR")}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* 액션 버튼 */}
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleEditTask}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                수정
              </button>
              <button
                onClick={() => {
                  setTaskToDelete(selectedTask);
                  setShowDeleteAlert(true);
                  setShowDetailModal(false);
                }}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                삭제
              </button>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedTaskDetail(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ===== 렌더 ===== */
  const days = getDaysInMonth();
  const weekDays = ["일", "월", "화", "수", "목", "금", "토"];

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-600">
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold">
            {currentDate.toLocaleDateString("ko-KR", {
              year: "numeric",
              month: "long",
            })}
          </h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              오늘
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* 캘린더 */}
      <div className="bg-white rounded-lg shadow">
        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 border-b">
          {weekDays.map((day, index) => (
            <div
              key={day}
              className={`p-3 text-center text-sm font-medium ${
                index === 0
                  ? "text-red-500"
                  : index === 6
                  ? "text-blue-500"
                  : "text-gray-700"
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* 날짜 그리드 */}
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const dayEvents = getEventsForDate(day.fullDate);
            const dayOfWeek = index % 7;

            return (
              <div
                key={index}
                className={`min-h-[120px] p-2 border-r border-b hover:bg-gray-50 cursor-pointer ${
                  !day.isCurrentMonth ? "bg-gray-50" : ""
                } ${isToday(day.fullDate) ? "bg-blue-50" : ""}`}
                onClick={() => handleDateClick(day)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-sm font-medium ${
                      !day.isCurrentMonth
                        ? "text-gray-400"
                        : dayOfWeek === 0
                        ? "text-red-500"
                        : dayOfWeek === 6
                        ? "text-blue-500"
                        : "text-gray-700"
                    } ${
                      isToday(day.fullDate)
                        ? "bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                        : ""
                    }`}
                  >
                    {day.date}
                  </span>
                  {day.isCurrentMonth && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDate(day.fullDate);
                        setSelectedTask(null);
                        setShowTaskModal(true);
                      }}
                      className="opacity-0 hover:opacity-100 transition-opacity"
                    >
                      <Plus className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>

                {/* 이벤트 목록 */}
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((event, eventIndex) => (
                    <div
                      key={`${event.id}-${eventIndex}`}
                      onClick={(e) => handleTaskClick(event, e)}
                      className={`text-xs p-1 rounded truncate flex items-center justify-between group ${getCategoryColor(
                        event.category
                      )}`}
                    >
                      <span className="truncate flex items-center">
                        {!event.is_all_day && event.start_time && (
                          <span className="mr-1">
                            {(() => {
                              const timePart =
                                event.start_time.split("T")[1] ||
                                event.start_time;
                              return timePart.substring(0, 5);
                            })()}
                          </span>
                        )}
                        {event.title}
                        {event.attachment_count > 0 && (
                          <Paperclip className="w-3 h-3 ml-1 text-gray-400" />
                        )}
                      </span>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100">
                        {getPriorityIcon(event.priority)}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTaskToDelete(event);
                            setShowDeleteAlert(true);
                          }}
                          className="hover:bg-black hover:bg-opacity-10 rounded p-0.5"
                        >
                          <MoreVertical className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-gray-500 text-center">
                      +{dayEvents.length - 3}개 더
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          isOpen={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedTask(null);
            setSelectedDate(null);
            setSelectedFiles([]);
          }}
          onSave={handleTaskSave}
          task={selectedTask}
          calendarId={selectedCalendars[0]} // 첫 번째 선택된 캘린더 사용
          onFileSelect={() => setShowFileModal(true)}
          selectedFiles={selectedFiles}
          initialDate={selectedDate} // 선택한 날짜 전달
        />
      )}

      {/* File Selector Modal */}
      <FileSelectorModal
        isOpen={showFileModal}
        onClose={() => setShowFileModal(false)}
        onSelect={(files) => {
          setSelectedFiles(files);
          setShowFileModal(false);
        }}
        multiple={true}
      />

      {/* Delete Alert */}
      <AlertModal
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setTaskToDelete(null);
        }}
        onConfirm={handleDeleteTask}
        title="일정 삭제"
        message="정말로 이 일정을 삭제하시겠습니까?"
        type="warning"
        confirmText="삭제"
        cancelText="취소"
      />

      {/* Detail Modal */}
      <TaskDetailModal />
    </div>
  );
}
